import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { environment } from '../../environments/environment';
import { ApiCredentials, Credential, NewCredential } from '../helpers/user';

import { ApiService } from './api.service';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    private credentials: Array<Credential>;

    private credentialsChangeSource: Subject<void> = new Subject<void>();
    private credentialsChange: Observable<void> = this.credentialsChangeSource.asObservable();

    private credentialsOpenSource: Subject<void> = new Subject<void>();
    private credentialsOpen: Observable<void> = this.credentialsOpenSource.asObservable();

    private keys: Array<string>;

    constructor(
        private apiService: ApiService,
    ) { }

    /**
     * Sets credential value.
     * @param newCredential New snippet credential object.
     */
    async addCredential(newCredential: NewCredential): Promise<void> {
        const credential: Credential = await this.apiService.post(`${environment.apiUrl}${environment.credentialsApi}`, newCredential);
        this.credentials.push(credential);

        this.credentialsChangeSource.next();
    }

    /**
     * List of available credential keys.
     */
    getCredentialOptions(): Array<string> {
        const options: Array<string> = this.keys.filter((key: string): boolean => {
            const credentialMatch: Credential = this.credentials.find((credential: Credential): boolean => {
                return credential.key === key;
            });

            return !credentialMatch;
        });

        return options;
    }

    /**
     * List of credential values.
     */
    getCredentials(): Array<Credential> {
        return this.credentials.map((credential: Credential): Credential => {
            return {
                ...credential
            };
        });
    }

    /**
     * Credentials change event.
     */
    getCredentialsChange(): Observable<void> {
        return this.credentialsChange;
    }

    /**
     * Credentials modal open event.
     */
    getCredentialsOpen(): Observable<void> {
        return this.credentialsOpen;
    }

    /**
     * Initializes service.
     */
    async init(): Promise<void> {
        await Promise.all([
            this.retrieveCredentialKeys(),
            this.retrieveCredentials()
        ]);
    }

    /**
     * Open credentials modal.
     */
    openCredentials(): void {
        this.credentialsOpenSource.next();
    }

    /**
     * Deletes credential value.
     * @param credentialToRemove Credential to be removed.
     */
    async removeCredential(credentialToRemove: Credential): Promise<void> {
        await this.apiService.delete(credentialToRemove.url);

        const index: number = this.credentials.findIndex((credential: Credential): boolean => {
            return credential.url === credentialToRemove.url;
        });

        if (index >= 0) {
            this.credentials.splice(index, 1);
        }

        this.credentialsChangeSource.next();
    }

    /**
     * Retrieves available credential keys.
     */
    async retrieveCredentialKeys(): Promise<void> {
        const keys: Array<string> = await this.apiService.get(`${environment.apiUrl}${environment.credentialsApi}keys/`);

        this.keys = keys.sort();
    }

    /**
     * Retrieves credential values.
     */
    async retrieveCredentials(): Promise<void> {
        const apiCredentials: ApiCredentials = await this.apiService.get(`${environment.apiUrl}${environment.credentialsApi}`);

        this.credentials = apiCredentials.results;
    }

    /**
     * Updates credential value.
     * @param credentialToUpdate Credential to be updated.
     */
    async updateCredential(credentialToUpdate: Credential): Promise<void> {
        await this.apiService.patch(credentialToUpdate.url, credentialToUpdate);

        const index: number = this.credentials.findIndex((credential: Credential): boolean => {
            return credential.url === credentialToUpdate.url;
        });

        if (index >= 0) {
            this.credentials[index] = credentialToUpdate;
        }

        this.credentialsChangeSource.next();
    }
}
